import { useEffect, useState } from "react";
import { CalendarPlus, CalendarCheck, CloudUpload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { calendarEvents, calendarAdd, calendarRemove, driveBackup } from "../../lib/api";
import { Button } from "../ui/button";
import { useGoogleStatus } from "./useGoogle";

let evCache = null;
const loadEvents = () => (evCache ? Promise.resolve(evCache) : calendarEvents().then((d) => { evCache = d; return d; }));
const bust = () => { evCache = null; };

// Small toggle on a reminder row: adds/removes the reminder on Google Calendar. Renders only when a Calendar-enabled account exists.
export const CalendarAddButton = ({ reminder }) => {
  const { calendarAccount } = useGoogleStatus();
  const [ev, setEv] = useState(evCache);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (calendarAccount) loadEvents().then(setEv).catch(() => {}); }, [calendarAccount]);
  if (!calendarAccount) return null;
  const on = (ev?.events || []).find((e) => e.document_id === reminder.document_id && e.date === reminder.date && e.label === reminder.label);
  const go = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try {
      if (on) { await calendarRemove(on.key); toast.success("Removed from Google Calendar"); }
      else { await calendarAdd({ document_id: reminder.document_id, date: reminder.date, label: reminder.label }); toast.success("Added to Google Calendar"); }
      bust(); setEv(await loadEvents());
    } catch (err) { toast.error(err?.response?.data?.detail || "Google Calendar didn't respond"); } finally { setBusy(false); }
  };
  const Icon = busy ? Loader2 : on ? CalendarCheck : CalendarPlus;
  return (
    <button type="button" data-testid={`reminder-calendar-${reminder.document_id}`} onClick={go} title={on ? "On Google Calendar — click to remove" : "Add to Google Calendar"} className={`shrink-0 rounded-md p-1 ${on ? "text-emerald-600" : "text-muted-foreground hover:text-primary"}`}>
      <Icon className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} />
    </button>
  );
};

// "Back up to Drive" for the executor PDF. Renders only when a Drive-enabled account exists.
export const DriveBackupButton = () => {
  const { driveAccount } = useGoogleStatus();
  const [busy, setBusy] = useState(false);
  if (!driveAccount) return null;
  const go = async () => {
    setBusy(true);
    try { const r = await driveBackup(driveAccount.id); toast.success("Executor PDF saved to Drive → HearthVault folder", r.backup?.link ? { action: { label: "Open", onClick: () => window.open(r.backup.link, "_blank") } } : undefined); }
    catch (e) { toast.error(e?.response?.data?.detail || "Backup failed"); } finally { setBusy(false); }
  };
  return (
    <Button variant="outline" className="gap-2" disabled={busy} data-testid="drive-backup-executor-btn" onClick={go}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CloudUpload className="h-4 w-4" />} Back up to Google Drive</Button>
  );
};
